import {
  FollowUpOutcome,
  FollowUpType,
  fromBackendFollowUpOutcome,
  fromBackendFollowUpType,
} from '@/constants/followUp'
import { JsonProperty, Serializable } from 'ts-jackson'
import { Fromable, PaginationResponse } from '../model'
import { OwnerUser } from '../lead/model'

export interface UpSellFollowUpSummary {
  overdue: number
  dueToday: number
  dueWithin7: number
}

export interface UpSellListResponse extends PaginationResponse<UpSellData> {
  followUpSummary?: UpSellFollowUpSummary
}

export interface CreateUpSellPayload {
  customerId: number
  interestedPackages: number[]
  opportunity: number
  expectedCloseValue: number
  expectedCloseDate: string
  saleId: number
}

export interface UpSellPackageInterestedPayload {
  interestedPackages: number[]
  opportunity: number
  expectedCloseValue: number
  expectedCloseDate: string
}
export interface UpSellStatusPayload {
  status: string
  reason: string
}

/** Step 1 of a follow-up (QTY-2095): book the appointment. */
export interface UpSellFollowUpPayload {
  followUpDate: string
  note: string
  type: FollowUpType
}

/** Step 2: the result, dated by the day it actually happened. */
export interface UpSellFollowUpResultPayload {
  followedUpAt: string
  outcome: FollowUpOutcome
  result: string
  note?: string
}

export interface UpdateStatusResponse {
  success: boolean
  toStatus: string
}

@Serializable()
export class UpSellPackageType extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('nameTh')
  nameTh!: string

  @JsonProperty('nameEn')
  nameEn!: string
}

@Serializable()
export class UpSellPackageInterested extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('name')
  name!: string

  @JsonProperty({ path: 'packageType', type: UpSellPackageType })
  packageType!: UpSellPackageType

  @JsonProperty('price')
  price!: number
}

@Serializable()
export class UpSellCustomerUser extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('code')
  code!: string

  @JsonProperty('citizenId')
  citizenId!: string

  @JsonProperty('firstName')
  firstName!: string

  @JsonProperty('lastName')
  lastName!: string

  @JsonProperty('nickname')
  nickname!: string

  @JsonProperty('gender')
  gender!: string

  @JsonProperty('dateOfBirth')
  dateOfBirth!: string

  @JsonProperty('email')
  email!: string

  @JsonProperty('phone')
  phone!: string
}

@Serializable()
export class UpSellStatusTransition extends Fromable {
  @JsonProperty('fromStatus')
  fromStatus?: string | null

  @JsonProperty('toStatus')
  toStatus!: string

  @JsonProperty('reason')
  reason?: string

  @JsonProperty('createdAt')
  createdAt!: string
}

@Serializable()
export class UpSellFollowUpStaffUser extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('firstName')
  firstName?: string

  @JsonProperty('lastName')
  lastName?: string

  @JsonProperty('nickname')
  nickname?: string
}

@Serializable()
export class UpSellFollowUp extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('upsellId')
  upsellId!: string

  @JsonProperty('staffId')
  staffId!: number

  @JsonProperty({ path: 'staffUser', type: UpSellFollowUpStaffUser })
  staffUser?: UpSellFollowUpStaffUser

  @JsonProperty('note')
  note!: string

  @JsonProperty('result')
  result?: string | null

  @JsonProperty({
    path: 'type',
    deserialize: (value: unknown) =>
      fromBackendFollowUpType(typeof value === 'string' ? value : null),
  })
  type?: FollowUpType | null

  @JsonProperty({
    path: 'outcome',
    deserialize: (value: unknown) =>
      fromBackendFollowUpOutcome(typeof value === 'string' ? value : null),
  })
  outcome?: FollowUpOutcome | null

  /** The appointment (วันนัดติดตาม). */
  @JsonProperty('nextActionAt')
  nextActionAt!: string

  /** The day the follow-up actually happened; null while pending. */
  @JsonProperty('occuredAt')
  occuredAt?: string | null

  @JsonProperty('createdAt')
  createdAt!: string

  /** Audit: when / by whom the result was recorded. */
  @JsonProperty('resultRecordedAt')
  resultRecordedAt?: string | null

  @JsonProperty({ path: 'resultStaffUser', type: UpSellFollowUpStaffUser })
  resultStaffUser?: UpSellFollowUpStaffUser
}

@Serializable()
export class UpSellData extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('createdAt')
  createdAt!: string

  /** Nearest pending appointment (historical name, same value as nextFollowUpAt). */
  @JsonProperty('followUpAt')
  followUpAt!: string | null

  /** Nearest still-pending appointment (QTY-2096); null when none is open. */
  @JsonProperty('nextFollowUpAt')
  nextFollowUpAt?: string | null

  /** Day of the latest recorded follow-up; null when none was recorded yet. */
  @JsonProperty('lastFollowedUpAt')
  lastFollowedUpAt?: string | null

  @JsonProperty('memberCode')
  memberCode!: string

  @JsonProperty('name')
  name!: string

  @JsonProperty('phone')
  phone!: string

  @JsonProperty({ path: 'packageInterested' })
  packageInterested!: string[]

  @JsonProperty('status')
  status!: string

  @JsonProperty('note')
  note?: string

  @JsonProperty('isFollowUpPassed')
  isFollowUpPassed!: boolean

  @JsonProperty('email')
  email?: string | null

  /** Win probability, 0-100. */
  @JsonProperty('opportunity')
  opportunity?: number | null

  /** Baht, not satang. */
  @JsonProperty('expectedCloseValue')
  expectedCloseValue?: number

  @JsonProperty('expectedCloseDate')
  expectedCloseDate?: string | null

  /** When the opportunity last moved into CLOSE_WON / CLOSE_LOST. */
  @JsonProperty('closedDate')
  closedDate?: string | null

  /** Owning salesperson's name, resolved by the API. */
  @JsonProperty('salesOwner')
  salesOwner?: string
}

@Serializable()
export class UpSellDetail extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('status')
  status!: string

  @JsonProperty('interestedPackages')
  interestedPackages!: number[]

  @JsonProperty({ path: 'packageInterested', elementType: UpSellPackageInterested })
  packageInterested!: UpSellPackageInterested[]

  @JsonProperty('expectedClosedAt')
  expectedClosedAt!: string

  @JsonProperty('expectedCloseValueAmount')
  expectedCloseValueAmount!: number

  @JsonProperty('expectedcloseValueCurrency')
  expectedCloseValueCurrency!: string

  @JsonProperty('opportunity')
  opportunity!: number

  @JsonProperty('note')
  note?: string | null

  @JsonProperty('notedAt')
  notedAt?: string | null

  @JsonProperty('customerUser')
  customerUser!: UpSellCustomerUser

  @JsonProperty({ path: 'upsellStatusTransition', elementType: UpSellStatusTransition })
  upsellStatusTransition!: UpSellStatusTransition[]

  @JsonProperty('createdAt')
  createdAt!: string

  @JsonProperty('updatedAt')
  updatedAt!: string

  @JsonProperty('deletedAt')
  deletedAt?: string | null

  @JsonProperty('ownerUserId')
  ownerUserId!: number

  @JsonProperty({ path: 'ownerUser', type: OwnerUser })
  ownerUser?: OwnerUser

  @JsonProperty('branchId')
  branchId!: number

  @JsonProperty('customerUserId')
  customerUserId!: number

  @JsonProperty('notedByUserId')
  notedByUserId?: number | null

  @JsonProperty('createdByUserId')
  createdByUserId!: number

  @JsonProperty('rejectedReason')
  rejectedReason?: string | null

  @JsonProperty('rejectedAt')
  rejectedAt?: string | null
}
